// FLEET LIVE's canvas colours (#6778), read off the dashboard's semantic tokens
// (`src/styles/_tokens.scss`) at paint time, so a theme flip repaints in the other theme's
// values with no second table to keep in step.
//
// TOKENS ONLY. The prototype's own variables map onto existing entries (brief.md item 52
// holds the table): its `--accent` (the working hue) is `--status-live`, NOT the
// dashboard's `--accent`, which is a neutral ink fill; `--glow` is the link hue
// (`--primary-text`); `--grid` is the panel rule; its `#FFFFFF` pill and hex fill is
// `--card` on paper and `--popover` (the raised panel) on void. Green is the shipped
// text hue: it paints merged and shipped work, and the log's "Fixed" chip the issue lists
// with them. A machine is never green, so healthy is never green.
//
// The accent is LIVE STATE only (`scripts/accent-discipline.test.ts`, this file's ALLOW
// row): it paints running work, a busy machine's dot and hex, and the live links.

import type { LiveTone } from './fleet-live';

export type LivePalette = {
  /** Running work, a busy machine, a live link: the working hue. */
  readonly live: string;
  /** The deck's scan line. */
  readonly glow: string;
  readonly text: string;
  readonly t2: string;
  readonly t3: string;
  readonly line: string;
  readonly rule: string;
  readonly grid: string;
  /** A pill's and a hexagon's fill. */
  readonly fill: string;
  readonly amber: string;
  readonly red: string;
  readonly green: string;
  readonly dark: boolean;
};

/** Read every colour through `read` (a custom-property lookup on the section). */
export function readLivePalette(read: (name: string) => string, dark: boolean): LivePalette {
  return {
    live: read('--status-live'),
    glow: read('--primary-text'),
    text: read('--foreground'),
    t2: read('--muted-foreground'),
    t3: read('--fg-60'),
    line: read('--border'),
    rule: read('--rule'),
    grid: read('--rule'),
    fill: read(dark ? '--popover' : '--card'),
    amber: read('--warning-text'),
    red: read('--destructive-text'),
    green: read('--success-text'),
    dark,
  };
}

/** The palette off an element's computed style; `dark` from the theme's `color-scheme`. */
export function paletteOf(el: Element): LivePalette {
  const cs = getComputedStyle(el);
  return readLivePalette(
    (name) => cs.getPropertyValue(name).trim(),
    cs.getPropertyValue('color-scheme').includes('dark'),
  );
}

/** A tone's colour. */
export function toneColor(p: LivePalette, tone: LiveTone): string {
  switch (tone) {
    case 'live':
      return p.live;
    case 'parked':
      return p.amber;
    case 'red':
      return p.red;
    case 'shipped':
      return p.green;
    case 'faint':
      return p.t3;
  }
}
